import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import EmailIcon from '@mui/icons-material/Email';
import { resendVerificationEmail } from '../../services/loyaltyEmailVerificationApi';
import { FONT_FAMILY } from '../../theme';

export default function EmailVerificationResend() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, sending, sent, error
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(0);

    // Pre-fill email from URL params
    useEffect(() => {
        const emailParam = searchParams.get('email');
        if (emailParam) {
            setEmail(emailParam);
        }
    }, [searchParams]);

    // Countdown timer for rate limiting feedback
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleResend = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!email || !email.includes('@')) {
            setError(t('loyalty.verification.invalidEmail') || 'Please enter a valid email address');
            return;
        }

        try {
            setStatus('sending');
            const response = await resendVerificationEmail(email);
            setStatus('sent');
            setMessage(response.message || t('loyalty.verification.resendSuccess') || 'If an account exists with that email, you will receive a verification email shortly.');
            setCountdown(60); // 1 minute cooldown
        } catch (err) {
            setStatus('error');
            if (err.message.includes('Too many attempts')) {
                setError(t('loyalty.verification.tooManyAttempts') || 'Too many attempts. Please try again in an hour.');
                setCountdown(3600); // 1 hour cooldown
            } else {
                setError(err.message || t('loyalty.verification.resendFailed') || 'Failed to resend verification email. Please try again.');
            }
        }
    };

    const formatCountdown = (seconds) => {
        if (seconds >= 3600) {
            const hours = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${mins}m`;
        }
        if (seconds >= 60) {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${mins}m ${secs}s`;
        }
        return `${seconds}s`;
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1e2225 0%, #2a2d31 100%)',
                padding: 3,
                fontFamily: FONT_FAMILY
            }}
        >
            <Card
                sx={{
                    maxWidth: 500,
                    width: '100%',
                    backgroundColor: '#2a2d31',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
            >
                <CardContent sx={{ padding: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <EmailIcon
                            sx={{
                                fontSize: 60,
                                color: '#ff6b00',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="h5"
                            sx={{
                                color: '#ffffff',
                                fontWeight: 600,
                                mb: 1,
                                fontFamily: FONT_FAMILY
                            }}
                        >
                            {t('loyalty.verification.resendTitle') || 'Resend Verification Email'}
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontFamily: FONT_FAMILY
                            }}
                        >
                            {t('loyalty.verification.resendDescription') || 'Enter your email address to receive a new verification link.'}
                        </Typography>
                    </Box>

                    {message && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 3,
                                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                color: '#4caf50',
                                '& .MuiAlert-icon': {
                                    color: '#4caf50'
                                }
                            }}
                        >
                            {message}
                        </Alert>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                color: '#f44336',
                                '& .MuiAlert-icon': {
                                    color: '#f44336'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleResend}>
                        <TextField
                            fullWidth
                            type="email"
                            label={t('loyalty.auth.email') || 'Email'}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={status === 'sending' || countdown > 0}
                            required
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#1e2225',
                                    color: '#ffffff',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#ff6b00',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#ff6b00',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    '&.Mui-focused': {
                                        color: '#ff6b00',
                                    },
                                },
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={status === 'sending' || countdown > 0}
                            sx={{
                                backgroundColor: '#ff6b00',
                                color: '#fff',
                                padding: '12px',
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                                mb: 2,
                                fontFamily: FONT_FAMILY,
                                '&:hover': {
                                    backgroundColor: '#ff8533',
                                },
                                '&:disabled': {
                                    backgroundColor: '#555',
                                    color: '#999',
                                }
                            }}
                        >
                            {status === 'sending' ? (
                                <CircularProgress size={24} sx={{ color: '#fff' }} />
                            ) : countdown > 0 ? (
                                `${t('loyalty.verification.waitToResend') || 'Wait'} ${formatCountdown(countdown)}`
                            ) : (
                                t('loyalty.verification.sendButton') || 'Send Verification Email'
                            )}
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate('/loyalty/login')}
                            sx={{
                                borderColor: '#ff6b00',
                                color: '#ff6b00',
                                padding: '12px',
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                                fontFamily: FONT_FAMILY,
                                '&:hover': {
                                    borderColor: '#ff8533',
                                    backgroundColor: 'rgba(255, 107, 0, 0.1)',
                                }
                            }}
                        >
                            {t('loyalty.verification.backToLogin') || 'Back to Login'}
                        </Button>
                    </form>

                    {status === 'sent' && (
                        <Alert
                            severity="info"
                            sx={{
                                mt: 3,
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                color: '#2196f3',
                                '& .MuiAlert-icon': {
                                    color: '#2196f3'
                                }
                            }}
                        >
                            {t('loyalty.verification.checkSpam') || 'Didn\'t receive the email? Check your spam folder or try again in an hour.'}
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
